import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2, X, TrendingUp, Bookmark, BookmarkPlus, Trash2, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { portraitFor } from "@/constants/images";

const SAMPLES = [
  "طفل تخاطب حضوره أقل من 70٪",
  "أطفال توحد",
  "تقدم IEP أعلى من 80٪",
];

export default function SmartSearchBar() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const navigate = useNavigate();
  const ref = useRef(null);

  const loadSaved = async () => {
    try { const { data } = await api.get("/saved-queries"); setSaved(data); }
    catch (_) { /* ignore */ }
  };

  useEffect(() => { loadSaved(); }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const run = async (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setQ(text);
    setBusy(true); setOpen(true);
    try {
      const { data } = await api.post("/search/smart", { query: text });
      setResults(data);
    } catch (e) {
      toast.error(extractError(e));
      setResults({ filters: {}, matches: [] });
    } finally { setBusy(false); }
  };

  const saveCurrent = async () => {
    if (!q.trim()) return;
    const label = window.prompt("اسم مختصر للاستعلام:", q.trim().slice(0, 40));
    if (!label) return;
    try {
      await api.post("/saved-queries", { label, query: q.trim() });
      toast.success("تم حفظ الاستعلام");
      await loadSaved();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeSaved = async (id) => {
    try {
      await api.delete(`/saved-queries/${id}`);
      await loadSaved();
    } catch (e) { toast.error(extractError(e)); }
  };

  const shareSaved = async (s) => {
    try {
      const { data } = await api.post(`/saved-queries/${s.id}/share`);
      toast.success(`تم إرسال النتائج إلى ${data.teachers_notified} معلم — ${data.matches} طفل مطابق`);
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div ref={ref} className="relative flex-1 max-w-xl" data-testid="smart-search">
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="relative">
        <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          data-testid="smart-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="ابحث ذكياً: طفل تخاطب حضوره أقل من 70٪..."
          className="h-11 pr-10 pl-16 bg-white border-slate-200"
        />
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setResults(null); }}
              className="w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 flex items-center justify-center"
              aria-label="مسح"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {q && (
            <button
              type="button"
              onClick={saveCurrent}
              data-testid="save-query-btn"
              className="w-7 h-7 rounded-md text-sky-700 hover:bg-sky-50 flex items-center justify-center"
              title="حفظ الاستعلام"
            >
              <BookmarkPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {saved.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="saved-queries-row">
          <Bookmark className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          {saved.slice(0, 5).map((s) => (
            <div key={s.id} className="group inline-flex items-center bg-white rounded-full border border-slate-200 hover:border-sky-300 transition-all shrink-0">
              <button
                data-testid={`saved-${s.id}`}
                onClick={() => run(s.query)}
                className="pr-3 pl-2 py-1 text-xs font-bold text-slate-700 hover:text-sky-700"
              >{s.label}</button>
              <button
                data-testid={`share-${s.id}`}
                onClick={() => shareSaved(s)}
                className="px-1.5 py-1 text-slate-400 hover:text-emerald-600 transition-colors"
                aria-label="مشاركة مع المعلمين"
                title="أرسل النتائج للمعلمين المعنيين"
              >
                <Share2 className="w-3 h-3" />
              </button>
              <button
                onClick={() => removeSaved(s.id)}
                className="pl-2 pr-1 py-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-rose-600 transition-opacity"
                aria-label="حذف"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute top-full mt-2 right-0 left-0 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 max-h-[70vh] overflow-y-auto">
          {busy ? (
            <div className="p-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> جاري تحليل السؤال...
            </div>
          ) : !results ? (
            <div className="p-4">
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">أمثلة</div>
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  data-testid={`sample-${s.slice(0, 10)}`}
                  onClick={() => { setQ(s); run(s); }}
                  className="w-full text-right px-3 py-2 rounded-lg hover:bg-sky-50 text-sm text-slate-700"
                >{s}</button>
              ))}
            </div>
          ) : (
            <div>
              {results.filters && (
                <div className="p-3 bg-sky-50/50 border-b border-sky-100 flex flex-wrap gap-1.5 text-xs">
                  <span className="text-slate-500 font-bold">فلاتر:</span>
                  {Object.entries(results.filters).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                    <Badge key={k} className="bg-white text-sky-700 border border-sky-200 hover:bg-white font-mono text-[10px]">{k}: {String(v)}</Badge>
                  ))}
                </div>
              )}
              {results.matches.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">لا توجد نتائج مطابقة</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {results.matches.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`search-match-${c.id}`}
                      onClick={() => { setOpen(false); navigate("/children"); }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-right"
                    >
                      <img src={portraitFor(c.id)} alt="" className="w-11 h-11 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate">{c.disability_type}</div>
                      </div>
                      {c.attendance_rate !== null && c.attendance_rate !== undefined && (
                        <div className="text-left">
                          <div className="text-xs text-slate-500">حضور {c.month}</div>
                          <div className="font-black text-emerald-700 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {c.attendance_rate}%</div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
